<template>
    <section id="contact" class="estimate-section">
        <div class="container estimate-layout">
            <div class="estimate-introduction">
                <p class="eyebrow">
                    <span></span>
                    {{ t.form.eyebrow }}
                </p>

                <h2>
                    {{ t.form.titleFirst }}
                    <em>{{ t.form.titleSecond }}</em>
                </h2>

                <p class="introduction-text">
                    {{ t.form.description }}
                </p>

                <div class="estimate-note">
                    <strong>{{ t.form.freeEstimate }}</strong>
                    <span>{{ t.form.note }}</span>
                </div>

                <div class="direct-contact">
                    <a href="tel:6629854708">
                        <div class="contact-icon">
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.7 19.7 0 0 1-8.6-3.1 19.4 19.4 0 0 1-6-6A19.7 19.7 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.8a2 2 0 0 1-.4 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z" />
                            </svg>
                        </div>

                        <div>
                            <span>{{ t.common.callUs }}</span>
                            <strong>662-985-4708</strong>
                        </div>
                    </a>

                    <a href="mailto:MMPConcrete@gmail.com">
                        <div class="contact-icon">
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <rect x="3" y="5" width="18" height="14" rx="2" />
                                <path d="m3 7 9 6 9-6" />
                            </svg>
                        </div>

                        <div>
                            <span>Email</span>
                            <strong>MMPConcrete@gmail.com</strong>
                        </div>
                    </a>
                </div>
            </div>

            <form class="estimate-form" @submit.prevent="submitForm">
                <div class="form-row">
                    <label>
                        {{ t.form.name }}

                        <input v-model.trim="form.name"
                               type="text"
                               name="name"
                               :placeholder="t.form.namePlaceholder"
                               autocomplete="name"
                               required />
                    </label>

                    <label>
                        {{ t.form.phone }}

                        <input v-model.trim="form.phone"
                               type="tel"
                               name="phone"
                               :placeholder="t.form.phonePlaceholder"
                               autocomplete="tel"
                               required />
                    </label>
                </div>

                <div class="form-row">
                    <label>
                        {{ t.form.email }}

                        <input v-model.trim="form.email"
                               type="email"
                               name="email"
                               :placeholder="t.form.emailPlaceholder"
                               autocomplete="email"
                               required />
                    </label>

                    <label>
                        {{ t.form.location }}

                        <input v-model.trim="form.city"
                               type="text"
                               name="city"
                               :placeholder="t.form.locationPlaceholder"
                               autocomplete="postal-code"
                               required />
                    </label>
                </div>

                <div class="form-row">
                    <label>
                        {{ t.form.projectType }}

                        <select v-model="form.projectType"
                                name="projectType"
                                required>
                            <option disabled value="">
                                {{ t.form.selectType }}
                            </option>

                            <option value="residential">
                                {{ t.form.residential }}
                            </option>

                            <option value="commercial">
                                {{ t.form.commercial }}
                            </option>
                        </select>
                    </label>

                    <label>
                        {{ t.form.service }}

                        <select v-model="form.service"
                                name="service"
                                required>
                            <option disabled value="">
                                {{ t.form.selectService }}
                            </option>

                            <option v-for="(service, index) in t.services.items"
                                    :key="service.title"
                                    :value="index">
                                {{ service.title }}
                            </option>
                        </select>
                    </label>
                </div>

                <label>
                    {{ t.form.message }}

                    <textarea v-model.trim="form.message"
                              name="message"
                              rows="5"
                              :placeholder="t.form.messagePlaceholder"
                              required></textarea>
                </label>

                <button class="submit-button"
                        type="submit"
                        :disabled="sending">
                    {{ sending ? t.form.sending : t.form.submit }}

                    <svg v-if="!sending"
                         viewBox="0 0 24 24"
                         aria-hidden="true">
                        <path d="M5 12h14"></path>
                        <path d="m13 6 6 6-6 6"></path>
                    </svg>

                    <span v-else class="loading-indicator"></span>
                </button>

                <p v-if="status === 'success'"
                   class="form-status success"
                   role="status">
                    {{ t.form.success }}
                </p>

                <p v-if="status === 'error'"
                   class="form-status error"
                   role="alert">
                    {{ t.form.error }}
                </p>
            </form>
        </div>
    </section>
</template>

<script>
    import emailjs from 'emailjs-com'

    export default {
        name: 'EstimateForm',

        props: {
            t: {
                type: Object,
                required: true
            },

            language: {
                type: String,
                required: true
            },

            selectedService: {
                type: Number,
                default: null
            }
        },

        data() {
            return {
                sending: false,
                status: null,

                form: {
                    name: '',
                    email: '',
                    phone: '',
                    city: '',
                    projectType: '',
                    service: '',
                    message: ''
                }
            }
        },

        watch: {
            selectedService: {
                immediate: true,

                handler(serviceIndex) {
                    if (serviceIndex !== null) {
                        this.form.service = serviceIndex
                    }
                }
            }
        },

        methods: {
            async submitForm() {
                this.sending = true
                this.status = null

                const selectedService =
                    this.t.services.items[this.form.service]

                const projectType =
                    this.form.projectType === 'commercial'
                        ? this.t.form.commercial
                        : this.t.form.residential

                const templateParams = {
                    name: this.form.name,
                    email: this.form.email,
                    phone: this.form.phone,
                    city: this.form.city,
                    project_type: projectType,
                    service: selectedService.title,
                    message: this.form.message,
                    language: this.language.toUpperCase()
                }

                const serviceID = 'service_x81o8ud'
                const templateID = 'template_ldodle8'
                const publicKey = '3fVLT8xF3XhtQJ8qF'

                try {
                    await emailjs.send(
                        serviceID,
                        templateID,
                        templateParams,
                        publicKey
                    )

                    if (typeof window.gtag === 'function') {
                        window.gtag('event', 'conversion', {
                            send_to: 'AW-17306413033/kFL6CJSL2vEcEOnPq7xA'
                        })
                    }

                    this.status = 'success'
                    this.resetForm()
                } catch (error) {
                    console.error('EmailJS error:', error)
                    this.status = 'error'
                } finally {
                    this.sending = false
                }
            },

            resetForm() {
                this.form = {
                    name: '',
                    email: '',
                    phone: '',
                    city: '',
                    projectType: '',
                    service: '',
                    message: ''
                }
            }
        }
    }
</script>

<style scoped>
    .estimate-section {
        padding-block: var(--section-padding);
        background-color: var(--color-concrete);
    }

    .estimate-layout {
        display: grid;
        grid-template-columns: 0.9fr 1.1fr;
        gap: 8%;
    }

    .eyebrow {
        display: flex;
        align-items: center;
        gap: 13px;
        margin-bottom: 25px;
        color: var(--color-orange-dark);
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.21em;
        text-transform: uppercase;
    }

        .eyebrow span {
            width: 32px;
            height: 2px;
            background-color: currentColor;
        }

    h2 {
        max-width: 680px;
        margin: 0;
        color: var(--color-charcoal);
        font-family: var(--font-heading);
        font-size: clamp(50px, 5.2vw, 80px);
        font-weight: 400;
        letter-spacing: -0.045em;
        line-height: 0.98;
    }

        h2 em {
            display: block;
            color: var(--color-orange);
            font-weight: 400;
        }

    .introduction-text {
        max-width: 570px;
        margin: 34px 0;
        color: var(--color-muted);
        font-size: 16px;
        line-height: 1.75;
    }

    .estimate-note {
        display: flex;
        padding: 8px 0 8px 20px;
        flex-direction: column;
        gap: 8px;
        border-left: 3px solid var(--color-orange);
    }

        .estimate-note strong {
            color: var(--color-charcoal);
            font-size: 10px;
            letter-spacing: 0.14em;
            text-transform: uppercase;
        }

        .estimate-note span {
            color: var(--color-muted);
            font-size: 13px;
        }

    .direct-contact {
        display: grid;
        margin-top: 48px;
        gap: 18px;
    }

        .direct-contact a {
            display: flex;
            align-items: center;
            gap: 15px;
        }

    .contact-icon {
        display: grid;
        width: 46px;
        height: 46px;
        flex-shrink: 0;
        place-items: center;
        color: var(--color-orange-dark);
        border: 1px solid var(--color-border);
    }

        .contact-icon svg {
            width: 21px;
            fill: none;
            stroke: currentColor;
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-width: 1.5;
        }

    .direct-contact a > div:last-child {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .direct-contact span {
        color: var(--color-muted);
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 0.12em;
        text-transform: uppercase;
    }

    .direct-contact strong {
        color: var(--color-charcoal);
        font-size: 14px;
    }

    .estimate-form {
        padding: 45px;
        background-color: #e7e1d6;
    }

    .form-row {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 24px;
    }

    label {
        display: block;
        margin-bottom: 27px;
        color: var(--color-charcoal);
        font-size: 9px;
        font-weight: 800;
        letter-spacing: 0.12em;
        text-transform: uppercase;
    }

    input,
    select,
    textarea {
        width: 100%;
        margin-top: 8px;
        padding: 13px 2px;
        color: var(--color-charcoal);
        background-color: transparent;
        border: 0;
        border-bottom: 1px solid #aaa296;
        border-radius: 0;
        outline: none;
        font-size: 14px;
        letter-spacing: normal;
        text-transform: none;
        transition: border-color 0.2s ease;
    }

        input:focus,
        select:focus,
        textarea:focus {
            border-color: var(--color-orange);
        }

    textarea {
        min-height: 125px;
        resize: vertical;
    }

    .submit-button {
        display: flex;
        width: 100%;
        min-height: 55px;
        align-items: center;
        justify-content: center;
        gap: 16px;
        color: var(--color-white);
        background-color: var(--color-orange);
        border: 0;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.13em;
        text-transform: uppercase;
        transition: background-color 0.25s ease, transform 0.25s ease;
    }

        .submit-button:hover:not(:disabled) {
            background-color: var(--color-orange-dark);
            transform: translateY(-2px);
        }

        .submit-button:disabled {
            cursor: wait;
            opacity: 0.75;
        }

        .submit-button svg {
            width: 18px;
            fill: none;
            stroke: currentColor;
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-width: 1.8;
        }

    .loading-indicator {
        width: 18px;
        height: 18px;
        border: 2px solid rgba(255, 255, 255, 0.45);
        border-top-color: var(--color-white);
        border-radius: 50%;
        animation: loading 0.75s linear infinite;
    }

    .form-status {
        margin: 18px 0 0;
        padding: 14px;
        font-size: 13px;
        line-height: 1.5;
    }

        .form-status.success {
            color: #315f38;
            background-color: #dceadd;
            border-left: 3px solid #4c8555;
        }

        .form-status.error {
            color: #803c32;
            background-color: #f1dcd8;
            border-left: 3px solid #b65445;
        }

    @keyframes loading {
        to {
            transform: rotate(360deg);
        }
    }

    @media (max-width: 900px) {
        .estimate-layout {
            grid-template-columns: 1fr;
            gap: 60px;
        }
    }

    @media (max-width: 560px) {
        .estimate-form {
            padding: 32px 22px;
        }

        .form-row {
            grid-template-columns: 1fr;
            gap: 0;
        }

        .direct-contact strong {
            font-size: 12px;
        }
    }
</style>